import { supabase } from './supabaseClient';

// Razorpay service for handling payment operations
export class RazorpayService {
  constructor() {
    this.keyId = import.meta.env?.VITE_RAZORPAY_KEY_ID;
    
    if (!this.keyId) {
      console.warn('Razorpay Key ID not configured. Payment functionality will be limited.');
    }
  }

  // Load Razorpay script dynamically
  loadRazorpayScript() {
    return new Promise((resolve) => {
      const existingScript = document.getElementById('razorpay-script');
      
      if (existingScript) {
        resolve(true);
        return;
      }

      const script = document.createElement('script');
      script.id = 'razorpay-script';
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }

  // Create order via Supabase Edge Function
  async createOrder({ amount, currency = 'INR', receipt, notes }) {
    try {
      const { data, error } = await supabase?.functions?.invoke('create-razorpay-order', {
        body: {
          amount,
          currency,
          receipt,
          notes
        }
      });

      if (error) {
        throw new Error(`Order creation failed: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error creating Razorpay order:', error);
      throw error;
    }
  }

  // Verify payment via Supabase Edge Function
  async verifyPayment({ 
    razorpay_payment_id, 
    razorpay_order_id, 
    razorpay_signature,
    registrationData 
  }) {
    try {
      const { data, error } = await supabase?.functions?.invoke('verify-razorpay-payment', {
        body: {
          razorpay_payment_id,
          razorpay_order_id,
          razorpay_signature,
          registrationData
        }
      });

      if (error) {
        throw new Error(`Payment verification failed: ${error.message}`);
      }

      return data;
    } catch (error) {
      console.error('Error verifying Razorpay payment:', error);
      throw error;
    }
  }

  // Open Razorpay checkout
  async openCheckout({ 
    order, 
    customerInfo, 
    onSuccess, 
    onError,
    onDismiss 
  }) {
    if (!this.keyId) {
      throw new Error('Razorpay Key ID not configured');
    }

    const scriptLoaded = await this.loadRazorpayScript();
    if (!scriptLoaded || !window.Razorpay) {
      throw new Error('Failed to load Razorpay script');
    }

    const options = {
      key: this.keyId,
      amount: order?.amount,
      currency: order?.currency || 'INR',
      name: 'Solvarsity',
      description: 'Training Program Registration',
      order_id: order?.id,
      handler: (response) => {
        onSuccess?.(response);
      },
      prefill: {
        name: customerInfo?.name || '',
        email: customerInfo?.email || '',
        contact: customerInfo?.phone || ''
      },
      theme: {
        color: '#3B82F6' // Primary color from your design system
      },
      modal: {
        ondismiss: () => {
          onDismiss?.();
        }
      }
    };

    try {
      const razorpay = new window.Razorpay(options);
      razorpay?.on('payment.failed', (response) => {
        onError?.(response?.error);
      });
      razorpay?.open();
    } catch (error) {
      console.error('Error opening Razorpay checkout:', error);
      onError?.(error);
    }
  }

  // Get fee structure based on profile
  getFeeStructure() {
    return {
      'it-support': { 
        amount: 4999, 
        display: '₹4,999',
        title: 'IT Support Engineer'
      },
      'software-developer': { 
        amount: 6999, 
        display: '₹6,999',
        title: 'Software Developer'
      }
    };
  }

  // Generate receipt ID
  generateReceiptId(prefix = 'reg') {
    const timestamp = Date.now();
    const random = Math.random()?.toString(36)?.substr(2, 9);
    return `${prefix}_${timestamp}_${random}`;
  }
}

// Export singleton instance
export const razorpayService = new RazorpayService();